/* Firebase Push client (storefront).

   Responsibilities:
   1. Wait for the Service Worker registration event raised by
      `firebase-sw-register.js`.
   2. Request notification permission via the native browser prompt.
   3. Acquire the FCM web token and publish a `RegisterDevice` event through the
      Gateway WebSocket so Security persists it in the `DeviceInfo` collection
      with Platform = "Web". The same Kafka topic used by mobile clients is
      reused so no new backend contracts are needed.
   4. Bind `messaging.onMessage` so that pushes arriving while any tab is
      open (foreground or minimized browser) are surfaced as OS-level
      notifications via `registration.showNotification`. Background pushes
      (no client open) are handled by the Service Worker via
      `onBackgroundMessage`. Firebase routes each push to exactly one of
      the two handlers, so there is no risk of double display.

   The token is sent over a short-lived WebSocket dedicated to the
   registration; reusing the long-lived `BookInWebSocket` would couple this
   feature to the extension-assets bundle, which is built and deployed
   separately. */

(function () {
  'use strict';

  var STORAGE_KEY = 'hola.push.lastToken';
  var TOKEN_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 days
  // SDK version must match firebase-messaging-sw.js importScripts version.
  // Both files use 11.0.1 (compat SDK). Do not upgrade one without the other.
  // The Shopify-Admin repo's firebase-messaging-sw.js uses a different version
  // (different deployment); only consistency within THIS Theme repo matters.
  var SDK_BASE = 'https://www.gstatic.com/firebasejs/11.0.1/';
  // Icon and badge URLs are provided by the Notifications backend via the
  // FCM data payload (WebPushSettings in appsettings.json). No hardcoded
  // fallbacks — if the backend doesn't send them, the OS default is used.
  // Fully-qualified Kafka topic the Gateway forwards RegisterDevice
  // messages to. Must match RentalServiceEvent.DEVICE_REGISTER used by
  // the rest of the storefront and the Security Kafka consumer. A
  // shorter alias like 'RegisterDevice' is NOT a topic name — the
  // Gateway publishes it verbatim to Kafka, it lands on a topic with no
  // consumer, and DeviceInfo is never written.
  var REGISTER_DEVICE_EVENT = 'public.holacarrentals.security.device.register';

  function getShopifyVisitorCookie() {
    try {
      var match = document.cookie
        .split('; ')
        .find(function (row) { return row.indexOf('_shopify_y=') === 0; });
      if (!match) return '';
      return match.split('=')[1] || '';
    } catch (e) {
      return '';
    }
  }

  // Bell SVG (Material Design "notifications" icon).
  var BELL_SVG = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="24" height="24"><path d="M12 22c1.1 0 2-.9 2-2h-4c0 1.1.9 2 2 2zm6-6v-5c0-3.07-1.63-5.64-4.5-6.32V4c0-.83-.67-1.5-1.5-1.5s-1.5.67-1.5 1.5v.68C7.64 5.36 6 7.92 6 11v5l-2 2v1h16v-1l-2-2z"/></svg>';

  var BELL_DISMISS_KEY = 'hola.push.bellDismissedAt';
  var BELL_COOLDOWN_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

  function wasBellDismissedRecently() {
    try {
      var ts = parseInt(window.localStorage.getItem(BELL_DISMISS_KEY) || '0', 10);
      if (!ts) return false;
      return (Date.now() - ts) < BELL_COOLDOWN_MS;
    } catch (e) { return false; }
  }

  function markBellDismissed() {
    try { window.localStorage.setItem(BELL_DISMISS_KEY, String(Date.now())); }
    catch (e) { /* ignore */ }
  }

  // WI #10406: the visitor just answered the permission prompt (or Chrome blocked it
  // for them) in this session, so the "notifications are blocked" banner of
  // push-reactivation-banner.js must not show up a few pages later. Same session key
  // that banner reads (SESSION_HIDE_KEY); it re-evaluates on a later visit.
  var REACTIVATION_SESSION_HIDE_KEY = 'hideNotificationBannerSession';

  function suppressReactivationForSession(permission) {
    if (permission !== 'denied') return;
    try { window.sessionStorage.setItem(REACTIVATION_SESSION_HIDE_KEY, '1'); }
    catch (e) { /* ignore */ }
  }

  function showBellAndRequestPermission() {
    return new Promise(function (resolve) {
      if (document.getElementById('hola-push-bell')) {
        resolve('default');
        return;
      }

      // Insert the bell inside header__help, before the help image.
      // The header uses CSS Grid with grid-area:help, so the bell must
      // live inside header__help to stay in the correct grid cell.
      var helpEl = document.getElementById('help_button_header');
      if (!helpEl) {
        resolve('default');
        return;
      }

      var btn = document.createElement('div');
      btn.id = 'hola-push-bell';
      btn.setAttribute('role', 'button');
      btn.setAttribute('tabindex', '0');
      btn.setAttribute('aria-label', 'Notifications');
      btn.style.cssText = [
        'position:relative',
        'display:flex', 'align-items:center', 'justify-content:center',
        'width:1.75rem', 'height:1.75rem', 'margin-right:37.5px',
        'background:transparent', 'border:none', 'padding:0',
        'cursor:pointer', 'color:#fff',
        'animation:hola-bell-entrance 0.4s ease-out'
      ].join(';');
      btn.innerHTML = '<span class="hola-push-bell__icon">' + BELL_SVG + '</span><span class="hola-push-bell__dot"></span>';

      // Inject keyframes, shake animation and red dot once.
      if (!document.getElementById('hola-push-bell-style')) {
        var style = document.createElement('style');
        style.id = 'hola-push-bell-style';
        style.textContent =
          '@keyframes hola-bell-entrance{from{opacity:0;transform:scale(0.5)}to{opacity:1;transform:scale(1)}}' +
          '@keyframes hola-bell-shake{0%,100%{transform:rotate(0)}15%{transform:rotate(12deg)}30%{transform:rotate(-10deg)}45%{transform:rotate(6deg)}60%{transform:rotate(-3deg)}75%{transform:rotate(0)}}' +
          '#hola-push-bell svg{width:1.75rem;height:1.75rem}' +
          '.hola-push-bell__icon{display:flex;animation:hola-bell-shake 2s ease-in-out infinite;animation-delay:3s}' +
          '.hola-push-bell__dot{position:absolute;top:-2px;right:-2px;width:8px;height:8px;background:#ec0101;border-radius:9999px}' +
          '.scrolled-past-header #hola-push-bell{color:#140064}';
        document.head.appendChild(style);
      }

      // Insert as first child of header__help so it appears to the left
      // of the help image, inheriting the flex layout and grid position.
      helpEl.insertBefore(btn, helpEl.firstChild);

      function cleanup() {
        if (btn.parentNode) btn.parentNode.removeChild(btn);
      }

      btn.addEventListener('click', function (event) {
        // The bell is injected inside #help_button_header[data-open-sheet="sheet-phones"].
        // Without stopPropagation, the click bubbles to the document listener in
        // src/snippets/phones-component.liquid and opens the phones sheet (WI #8248).
        event.stopPropagation();
        try {
          var result = Notification.requestPermission();
          if (result && typeof result.then === 'function') {
            result.then(function (permission) {
              if (permission === 'granted' || permission === 'denied') {
                suppressReactivationForSession(permission); // WI #10406
                cleanup();
                resolve(permission);
              }
              // 'default' (closed without answering) — keep bell visible
            });
          } else {
            setTimeout(function () {
              var perm = Notification.permission;
              if (perm === 'granted' || perm === 'denied') {
                suppressReactivationForSession(perm); // WI #10406
                cleanup();
                resolve(perm);
              }
              // 'default' — keep bell visible
            }, 0);
          }
        } catch (e) {
          // keep bell visible on error
        }
      });
    });
  }

  if (!('serviceWorker' in navigator) || !('PushManager' in window) || !('Notification' in window)) {
    return;
  }

  // Email is optional. The storefront is mostly anonymous (Shopify customer
  // login is not required to book), so we register the FCM token first and
  // associate the email later when it becomes available (after a reservation
  // or login). Security's RegisterOrAssociateDeviceAsync is built for this:
  //   - new DeviceId  -> insert (with or without email)
  //   - existing DeviceId + new email -> associate
  //   - existing DeviceId + empty email -> disassociate (logout)
  // This unlocks both broadcast pushes by Platform="Web" (anonymous visitors)
  // and per-user pushes by email (post-reservation customers).
  var customerEmail = window.__HOLA_CUSTOMER_EMAIL__ || null;

  var firebaseConfig = window.__FIREBASE_CONFIG__;
  var vapidKey = window.__FIREBASE_VAPID_KEY__;
  var gatewayWsUrl = window.__HOLA_GATEWAY_WS_URL__;
  if (!firebaseConfig || !firebaseConfig.apiKey || firebaseConfig.apiKey.indexOf('$(') === 0) {
    return;
  }
  if (!vapidKey || vapidKey.indexOf('$(') === 0) {
    return;
  }
  if (!gatewayWsUrl || gatewayWsUrl.indexOf('$(') === 0) {
    return;
  }

  function loadScript(src) {
    return new Promise(function (resolve, reject) {
      var s = document.createElement('script');
      s.src = src;
      s.async = true;
      s.onload = function () { resolve(); };
      s.onerror = function () { reject(new Error('Failed to load ' + src)); };
      document.head.appendChild(s);
    });
  }

  function ensureSdk() {
    if (window.firebase && window.firebase.messaging) {
      return Promise.resolve();
    }
    return loadScript(SDK_BASE + 'firebase-app-compat.js')
      .then(function () { return loadScript(SDK_BASE + 'firebase-messaging-compat.js'); });
  }

  function waitForSwRegistration() {
    if (window.__FIREBASE_SW_REGISTRATION__) {
      return Promise.resolve(window.__FIREBASE_SW_REGISTRATION__);
    }
    return new Promise(function (resolve) {
      window.addEventListener('hola:firebase-sw-ready', function (event) {
        resolve(event.detail.registration);
      }, { once: true });
    });
  }

  function newGuid() {
    var template = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx';
    return template.replace(/[xy]/g, function (c) {
      var r = (Math.random() * 16) | 0;
      var v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }

  function publishRegisterDevice(token) {
    return new Promise(function (resolve, reject) {
      var sessionId = newGuid();
      // The Gateway routes WebSocket traffic to Spin.WebSite under the
      // `/ws/RT/` prefix (Ocelot reverse proxy). Connecting to the bare
      // `/Eda/EdaSocket/Connect` path hits nothing and the socket closes
      // immediately — which is what produced the "socket error" in the
      // previous build. The same path is used by the extension bundle in
      // bookInWebSocket.ts, so reuse it here.
      var url = gatewayWsUrl.replace(/\/$/, '') + '/ws/RT/Eda/EdaSocket/Connect?id=' + sessionId;
      var socket;
      try {
        socket = new WebSocket(url);
      } catch (err) {
        reject(err);
        return;
      }

      var settled = false;
      var done = function (ok, err) {
        if (settled) return;
        settled = true;
        try { socket.close(); } catch (e) { /* ignore */ }
        if (ok) resolve(); else reject(err || new Error('register-device failed'));
      };

      var timeout = setTimeout(function () { done(false, new Error('timeout')); }, 8000);

      socket.onopen = function () {
        var payload = {
          DeviceId: token,
          Email: customerEmail,
          Platform: 'Web',
          // Gateway's SocketWrapperMessage expects the device sessionUserId
          // to correlate this WS connection with the visitor's other
          // traffic. Security joins push devices to the same profile. The
          // rest of the storefront reads this from the _shopify_y cookie;
          // mirrored here so backend joins line up.
          sessionUserId: getShopifyVisitorCookie()
        };
        // `isCache: true` makes the Gateway's SendMessageUseCase publish the
        // event to its original Kafka topic instead of routing it through
        // the default Geolocation enrichment chain (which expects a
        // GetLocationDto and would silently drop this payload). The flag is
        // misnamed in the backend — its real semantics are "bypass
        // enrichment, publish to the requested topic" — but we reuse the
        // existing path rather than add an event-specific branch.
        //
        // Envelope shape must match Gateway's SocketWrapperMessage in full:
        // missing fields cause the deserializer to drop the message before
        // it ever reaches Kafka, which is what produced the silent failure
        // in the original implementation.
        var envelope = {
          event: REGISTER_DEVICE_EVENT,
          idSession: sessionId,
          data: JSON.stringify(payload),
          isCache: true,
          visitorId: undefined,
          oldCar: undefined,
          ip: '',
          geolocationData: undefined
        };
        try {
          socket.send(JSON.stringify(envelope));
          clearTimeout(timeout);
          // Give the gateway a tick to publish before closing.
          setTimeout(function () { done(true); }, 250);
        } catch (err) {
          clearTimeout(timeout);
          done(false, err);
        }
      };

      socket.onerror = function () { clearTimeout(timeout); done(false, new Error('socket error')); };
    });
  }

  // Aggressive permission request: tries immediately, then on scroll/touch,
  // falls back to bell click if browser requires explicit click gesture.
  function requestPermissionEarly() {
    return new Promise(function (resolve) {
      if (Notification.permission !== 'default') {
        resolve(Notification.permission);
        return;
      }

      var settled = false;
      function done(permission) {
        if (settled) return;
        settled = true;
        document.removeEventListener('scroll', onInteraction);
        document.removeEventListener('touchstart', onInteraction);
        console.log('[Notification] Resultado:', permission === 'granted' ? '✅ Activadas' : permission === 'denied' ? '❌ Rechazadas' : '⏸ Sin respuesta');
        suppressReactivationForSession(permission); // WI #10406
        resolve(permission);
      }

      function tryRequest(trigger) {
        if (settled) return;
        console.log('[Notification] Pidiendo permiso — trigger:', trigger);
        try {
          Notification.requestPermission().then(function (p) {
            if (p !== 'default') {
              done(p);
            }
            // if 'default' -> browser dismissed silently, wait for next trigger
          }).catch(function () { /* browser blocked, wait for interaction */ });
        } catch (e) {
          // Sync callback API (old browsers) — ignore, wait for interaction
        }
      }

      function onInteraction(e) {
        tryRequest(e.type);
      }

      // 1. Try immediately — works in some browsers without gesture
      tryRequest('immediate');

      // 2. If not settled, listen for scroll/touch
      if (!settled) {
        console.log('[Notification] Esperando interacción (scroll, touchstart)...');
        document.addEventListener('scroll', onInteraction, { once: true });
        document.addEventListener('touchstart', onInteraction, { once: true });
      }

      // 3. If after 5s still not settled, give up — bell click will handle it
      setTimeout(function () {
        if (!settled) {
          console.log('[Notification] Timeout — fallback a campanita (click)');
          document.removeEventListener('scroll', onInteraction);
          document.removeEventListener('touchstart', onInteraction);
          resolve('default'); // let bell handle it
        }
      }, 5000);
    });
  }

  function isDesktopSafariOrEdge() {
    var ua = navigator.userAgent;
    if (/Mobi|Android|iPhone|iPad|iPod/i.test(ua)) return false;
    var isSafari = /^((?!chrome|android).)*safari/i.test(ua);
    var isEdge = /Edg\//i.test(ua);
    return isSafari || isEdge;
  }

  function init() {
    console.log('[Notification] Init — estado:', Notification.permission);

    if (Notification.permission === 'denied') {
      console.warn('[Notification] ❌ Bloqueado por el usuario');
      return;
    }

    // If permission is already granted, proceed on every page.
    // If not granted, try on any page (not just /pages/).
    var isPromptPage = window.location.pathname.indexOf('/pages/') !== -1;
    if (Notification.permission !== 'granted' && !isPromptPage) {
      return;
    }

    // Skip the entire SDK + getToken + WebSocket chain when the cached
    // token is still fresh and the email hasn't changed. Saves ~180 KB
    // of Firebase SDK download on recurring visits.
    if (Notification.permission === 'granted') {
      try {
        var cachedToken = window.localStorage.getItem(STORAGE_KEY);
        var cachedTs = parseInt(window.localStorage.getItem(STORAGE_KEY + '.ts') || '0', 10);
        var cachedEmail = window.localStorage.getItem(STORAGE_KEY + '.email');
        var emailNow = customerEmail || '';
        if (cachedToken && cachedTs && (Date.now() - cachedTs) < TOKEN_TTL_MS && emailNow === (cachedEmail || '')) {
          bindLogoutDisassociation();
          return;
        }
      } catch (e) { /* localStorage unavailable — continue */ }
    }

    Promise.all([ensureSdk(), waitForSwRegistration()]).then(function (results) {
      var registration = results[1];

      var permissionPromise;
      if (Notification.permission === 'granted') {
        permissionPromise = Promise.resolve('granted');
      } else if (isDesktopSafariOrEdge()) {
        // Desktop Safari and Edge require an explicit click gesture for
        // Notification.requestPermission(). Scroll, touch, and immediate
        // triggers are rejected — go straight to the bell button.
        console.log('[Notification] Desktop Safari/Edge — campanita directa');
        permissionPromise = showBellAndRequestPermission();
      } else {
        // Try aggressive request first (immediate + scroll/touch)
        // If that fails, fall back to bell click
        permissionPromise = requestPermissionEarly().then(function (p) {
          if (p === 'granted') return 'granted';
          // WI #10406: once blocked, requestPermission() answers 'denied' without a
          // prompt, so the bell would do nothing — do not show it.
          if (p === 'denied') return 'denied';
          // Browser required explicit click — show the bell
          console.log('[Notification] Mostrando campanita para click explícito');
          return showBellAndRequestPermission();
        });
      }

      permissionPromise.then(function (permission) {
        if (permission !== 'granted') {
          return;
        }

        if (!window.firebase.apps || window.firebase.apps.length === 0) {
          window.firebase.initializeApp(firebaseConfig);
        }
        var messaging = window.firebase.messaging();

        messaging.getToken({ vapidKey: vapidKey, serviceWorkerRegistration: registration })
          .then(function (token) {
            if (!token) return;
            // Skip when nothing relevant changed since last register. We track
            // both the FCM token and the email so we re-register when the user
            // becomes identified (post-reservation/login). We do NOT re-send
            // when the page is anonymous and the device was previously
            // associated to an email — sending an empty email would trigger
            // the backend's logout-disassociation path on every anonymous
            // visit, which is wrong for a storefront where "no Shopify login"
            // is the normal state, not a logout.
            var lastToken = null;
            var lastEmail = null;
            try {
              lastToken = window.localStorage.getItem(STORAGE_KEY);
              lastEmail = window.localStorage.getItem(STORAGE_KEY + '.email');
            } catch (e) { /* ignore */ }

            var tokenUnchanged = token === lastToken;
            var emailUnchanged = (customerEmail || '') === (lastEmail || '');
            if (tokenUnchanged && emailUnchanged) return;

            // Anonymous load + previously associated to an email -> skip.
            // The association persists on the backend until the user actively
            // re-identifies (next reservation or login).
            if (tokenUnchanged && !customerEmail && lastEmail) return;

            return publishRegisterDevice(token).then(function () {
              try {
                window.localStorage.setItem(STORAGE_KEY, token);
                window.localStorage.setItem(STORAGE_KEY + '.email', customerEmail || '');
                window.localStorage.setItem(STORAGE_KEY + '.ts', String(Date.now()));
              } catch (e) { /* ignore */ }
            });
          })
          .catch(function (err) {
            console.warn('[firebase-push-client] getToken failed', err);
          });

        // Firebase Web SDK does NOT auto-display notifications from
        // `onMessage` (only from `onBackgroundMessage` in the SW). To honor
        // the product requirement "push must appear as an OS notification
        // regardless of tab focus or window state", we trigger
        // `showNotification` manually here using the same SW registration
        // that owns the push subscription. This surfaces the native OS
        // notification even when the browser is minimized or the user is
        // on a different tab.
        messaging.onMessage(function (payload) {
          // Diagnostic log: confirms the browser received the push while
          // at least one tab was open (FCM foreground path). If this line
          // never prints but the backend reports success, the problem is
          // between FCM and the browser (token stale, SW not subscribed,
          // Chrome notifications throttled, etc.).
          console.info('[firebase-push-client] onMessage payload', payload);
          try {
            var notification = payload.notification || {};
            var data = payload.data || {};
            var title = notification.title || data.title || 'HolaCarRentals';
            var options = {
              body: notification.body || data.body || '',
              data: { url: data.url || (notification.click_action || '/') }
            };
            if (data.icon) options.icon = data.icon;
            if (data.badge) options.badge = data.badge;
            registration.showNotification(title, options).then(function () {
              console.info('[firebase-push-client] showNotification resolved');
            }).catch(function (err) {
              console.warn('[firebase-push-client] showNotification rejected', err);
            });
          } catch (e) {
            console.warn('[firebase-push-client] showNotification failed', e);
          }
        });

        // T4: Refresh FCM token when Firebase rotates it. Re-register with the
        // new token so the Security service's DeviceInfo stays current.
        // Firebase v9+ removed onTokenRefresh — token auto-refreshes via getToken().
        // Defensive check keeps backward compat with v8 while preventing crash on v9+.
        if (typeof messaging.onTokenRefresh === 'function') {
          messaging.onTokenRefresh(function () {
            messaging.getToken({ vapidKey: vapidKey, serviceWorkerRegistration: registration })
              .then(function (newToken) {
                if (!newToken) return;
                return publishRegisterDevice(newToken).then(function () {
                  try {
                    window.localStorage.setItem(STORAGE_KEY, newToken);
                    window.localStorage.setItem(STORAGE_KEY + '.email', customerEmail || '');
                    window.localStorage.setItem(STORAGE_KEY + '.ts', String(Date.now()));
                  } catch (e) { /* ignore */ }
                });
              })
              .catch(function (err) { console.warn('[firebase-push-client] onTokenRefresh getToken failed', err); });
          });
        }

        // T3: Disassociate device from user email on logout so the backend
        // stops routing per-user pushes to this device. Navigation is NOT
        // blocked — event.preventDefault() is intentionally absent.
        bindLogoutDisassociation();
      });
    }).catch(function (err) {
      console.warn('[firebase-push-client] init failed', err);
    });
  }

  // T3: Bind click/submit handlers on all logout links and forms.
  // Runs only after permission is granted (called inside permissionPromise.then).
  // Sets customerEmail = null and calls publishRegisterDevice with the stored
  // token so the Security service's RegisterOrAssociateDeviceAsync runs the
  // disassociation path (existing DeviceId + empty email). The WebSocket call
  // is fire-and-forget — we do not await it so the logout navigation proceeds
  // immediately.
  function bindLogoutDisassociation() {
    var logoutEls = document.querySelectorAll(
      'a[href*="/account/logout"], form[action*="/account/logout"]'
    );
    for (var i = 0; i < logoutEls.length; i++) {
      (function (el) {
        var eventType = el.tagName.toLowerCase() === 'form' ? 'submit' : 'click';
        el.addEventListener(eventType, function () {
          var storedToken = null;
          try { storedToken = window.localStorage.getItem(STORAGE_KEY); } catch (e) { /* ignore */ }
          if (storedToken) {
            customerEmail = null;
            // Fire-and-forget: do not block logout navigation.
            publishRegisterDevice(storedToken);
            try {
              window.localStorage.removeItem(STORAGE_KEY);
              window.localStorage.removeItem(STORAGE_KEY + '.email');
              window.localStorage.removeItem(STORAGE_KEY + '.ts');
            } catch (e) { /* ignore */ }
          }
          // Do NOT call event.preventDefault() — logout must proceed.
        });
      })(logoutEls[i]);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
